import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CallCenterStatsService } from './call-center-stats.service';
import { AuthService } from './auth.service';
import { CallLogRequest } from '../interfaces/call-center-stats.interface';
import { CallCenterApiService } from './call-center-api.service';
import { CallCenterStateService } from './call-center-state.service';
import { ReminderService } from './reminder.service';
import { NotificationPollingService } from './notification-polling.service';
import { CallCenterDatabaseTableData } from '../interfaces/call-center-database.interface';
import { InputDialogResult } from '../interfaces/input-dialog.interface';

/**
 * CallCenterBusinessService
 * 
 * Üzleti logika és orkesztráció.
 * Felelősség: UI interakciók, stats logging, API és state koordinálás.
 */
@Injectable({
  providedIn: 'root'
})
export class CallCenterBusinessService {

  constructor(
    private apiService: CallCenterApiService,
    private stateService: CallCenterStateService,
    private callCenterStatsService: CallCenterStatsService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private reminderService: ReminderService,
    private notificationPollingService: NotificationPollingService
  ) {}

  // === BUSINESS OPERATIONS ===

  /**
   * Cég státusz frissítése komplét üzleti logikával
   */
  updateCompanyStatusWithBusinessLogic(companyId: number, statusData: any): Observable<any> {
    return this.apiService.updateCompanyStatus(companyId, statusData);
  }

  /**
   * Vezető hozzáadása és sikeres visszajelzés
   */
  addCompanyOfficialWithFeedback(companyId: number, officialName: string): Observable<any> {
    const result = this.apiService.addCompanyOfficial(companyId, officialName);
    
    result.subscribe({
      next: () => this.showSuccess(`Vezető "${officialName}" sikeresen hozzáadva`),
      error: (error) => this.showError(`Hiba a vezető hozzáadásánál: ${error.message}`)
    });
    
    return result;
  }

  /**
   * Telefon hozzáadása és sikeres visszajelzés
   */
  addCompanyPhoneWithFeedback(companyId: number, phoneNumber: string): Observable<any> {
    const result = this.apiService.addCompanyPhone(companyId, phoneNumber);
    
    result.subscribe({
      next: () => this.showSuccess(`Telefon "${phoneNumber}" sikeresen hozzáadva`),
      error: (error) => this.showError(`Hiba a telefon hozzáadásánál: ${error.message}`)
    });
    
    return result;
  }

  /**
   * Email hozzáadása és sikeres visszajelzés
   */
  addCompanyEmailWithFeedback(companyId: number, email: string): Observable<any> {
    const result = this.apiService.addCompanyEmail(companyId, email);
    
    result.subscribe({
      next: () => this.showSuccess(`Email "${email}" sikeresen hozzáadva`),
      error: (error) => this.showError(`Hiba az email hozzáadásánál: ${error.message}`)
    });
    
    return result;
  }

  /**
   * Több email hozzáadása és sikeres visszajelzés
   */
  addMultipleEmailsWithFeedback(companyId: number, emails: string[]): Observable<any[]> {
    const result = this.apiService.addMultipleEmails(companyId, emails);
    
    result.subscribe({
      next: () => this.showSuccess(`${emails.length} email sikeresen hozzáadva`),
      error: (error) => this.showError(`Hiba az emailek hozzáadásánál: ${error.message}`)
    });
    
    return result;
  }

  /**
   * Kapcsolat érvényességi státusz frissítése feedback-kel
   */
  updateContactValidStatusWithFeedback(companyId: number, updateRequest: any): Observable<any> {
    const result = this.apiService.updateContactValidStatus(companyId, updateRequest);
    
    result.subscribe({
      next: () => this.showSuccess('Kapcsolat státusz sikeresen frissítve'),
      error: (error) => this.showError(`Hiba a státusz frissítésénél: ${error.message}`)
    });
    
    return result;
  }

  /**
   * Több kapcsolat státusz frissítése feedback-kel
   */
  updateMultipleValidStatusesWithFeedback(companyId: number, validStatusUpdates: any): Observable<any[]> {
    const result = this.apiService.updateMultipleValidStatuses(companyId, validStatusUpdates);
    
    result.subscribe({
      next: () => this.showSuccess('Kapcsolatok státusza sikeresen frissítve'),
      error: (error) => this.showError(`Hiba a státuszok frissítésénél: ${error.message}`)
    });
    
    return result;
  }

  // === UI INTERACTION METHODS ===

  /**
   * Szöveg másolása vágólapra felhasználói visszajelzéssel
   */
  copyToClipboard(text: string): void {
    if (!text || text.trim() === '') {
      this.snackBar.open('Nincs másolható tartalom', 'Bezárás', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open(`Másolva: ${text}`, 'Bezárás', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.snackBar.open('Másolás sikertelen', 'Bezárás', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    });
  }

  /**
   * Call center statisztika naplózás
   */
  logCallCenterStats(status: string, propertyInsuranceAppointment: boolean = false): void {
    const statusMapping: { [key: string]: string } = {
      'visszahivando': 'callback',
      'elutasitott': 'rejected',
      'robinson_listas': 'robinson_list',
      'nem_vette_fel': 'no_answer',
      'egyeztetett': 'appointment_scheduled_actual',
      'egyeztetett-kum': 'appointment_scheduled_actual',
      'egyeztetett-vagyon': 'property_insurance_appointment_actual',
      'egyeztetett-kv': 'appointment_scheduled_and_property_insurance'
    };

    const backendStatus = statusMapping[status];
    
    if (!backendStatus) {
      return;
    }

    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser) {
      console.error('❌ Nincs bejelentkezett felhasználó');
      return;
    }

    const weekInfo = this.callCenterStatsService.getCurrentWeekInfo();
    const dayIndex = this.callCenterStatsService.getCurrentDayIndex();

    const callLogRequest: CallLogRequest = {
      user_id: currentUser.id,
      week_number: weekInfo.weekNumber,
      year: weekInfo.year,
      day_index: dayIndex,
      status: backendStatus,
      property_insurance_appointment: propertyInsuranceAppointment
    };

    this.callCenterStatsService.logCall(callLogRequest).subscribe({
      next: (response) => {
        // Success logging handled internally
      },
      error: (error: any) => {
        console.error('❌ Hiba a call center stats rögzítésénél:', error);
      }
    });
  }

  /**
   * Sikeres üzenet megjelenítése
   */
  showSuccess(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Hibaüzenet megjelenítése
   */
  showError(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  // === STATE COORDINATION ===

  /**
   * Egyetlen elem újratöltése és state frissítés
   */
  reloadSingleElementAndUpdateState(companyId: number): Observable<any> {
    return this.apiService.reloadSingleElement(companyId);
  }

  /**
   * Filter alkalmazás és state trigger
   */
  applyFiltersAndTriggerUpdate(filterData: any): void {
    this.stateService.applyFilterData(filterData);
    this.stateService.triggerFilterUpdate();
  }

  /**
   * Szűrők törlése és state reset
   */
  clearFiltersAndResetState(): void {
    this.stateService.clearFilters();
    this.stateService.clearSelection();
    this.stateService.triggerFilterUpdate();
  }

  // === DELEGATION METHODS (Direct access to API & State) ===

  /**
   * API service delegáció
   */
  get api() {
    return this.apiService;
  }

  /**
   * State service delegáció
   */
  get state() {
    return this.stateService;
  }

  // === DIALOG RESULT PROCESSING ===

  /**
   * Input-dialog eredmény feldolgozása (státusz frissítés + kontakt változások + emlékeztetők).
   * Újrafelhasználható a table komponensből és a headerből is.
   * A callback-ben kapott elem újratöltését az `onElementReload` függvény végzi (opcionális, a hívó adja meg).
   */
  processDialogResult(
    element: CallCenterDatabaseTableData,
    result: InputDialogResult,
    onElementReload?: (companyId: number) => void
  ): void {
    if (result.saveChangesOnly) {
      this.processSaveChangesOnly(element, result, onElementReload);
    } else if (result.selectedStatus) {
      this.processStatusUpdate(element, result, onElementReload);
    }
  }

  private processStatusUpdate(
    element: CallCenterDatabaseTableData,
    result: InputDialogResult,
    onElementReload?: (companyId: number) => void
  ): void {
    const newStatus = result.selectedStatus!;
    const statusUpdateData = {
      status: newStatus,
      notes: result.value,
      valid_status_updates: this.prepareValidStatusUpdates(result.validStatusUpdates),
      edited_officials: result.editedOfficials || undefined,
      edited_phones: result.editedPhones || undefined,
      edited_emails: result.editedEmails || undefined,
      reminder_time: result.reminderTime || undefined
    };

    const hasContactChanges = this.hasContactChanges(result);

    this.apiService.updateCompanyStatus(element.id, statusUpdateData).subscribe({
      next: (response) => {
        if (response.success) {
          this.logCallCenterStats(newStatus, result.assetInsurance || false);
          this.processCallQualityMetrics(result);
          this.showSuccess('Státusz sikeresen frissítve');
          if (!hasContactChanges && onElementReload) {
            onElementReload(element.id);
          }
        }
      },
      error: (error) => {
        this.showError(`Hiba történt a státusz frissítése során: ${error.message || error}`);
      }
    });

    this.processNewContacts(element, result, onElementReload);

    if (result.reminderTime) {
      this.createReminderForCallback(element, result);
    }
  }

  private processSaveChangesOnly(
    element: CallCenterDatabaseTableData,
    result: InputDialogResult,
    onElementReload?: (companyId: number) => void
  ): void {
    const hasContactChanges = this.hasContactChanges(result);
    const hasValidStatusUpdates = result.validStatusUpdates && Object.keys(result.validStatusUpdates).length > 0;
    const hasNotes = !!(result.value?.trim());
    const hasEditedPhones = result.editedPhones && Object.keys(result.editedPhones).length > 0;
    const hasEditedEmails = result.editedEmails && Object.keys(result.editedEmails).length > 0;

    if (hasValidStatusUpdates || hasNotes || hasEditedPhones || hasEditedEmails) {
      const statusUpdateData = {
        status: null as string | null,
        notes: result.value,
        valid_status_updates: this.prepareValidStatusUpdates(result.validStatusUpdates),
        edited_officials: result.editedOfficials || undefined,
        edited_phones: result.editedPhones || undefined,
        edited_emails: result.editedEmails || undefined
      };

      this.apiService.updateCompanyStatus(element.id, statusUpdateData).subscribe({
        next: (response) => {
          if (response.success) {
            this.showSuccess('Módosítások sikeresen mentve');
            if (!hasContactChanges && onElementReload) {
              onElementReload(element.id);
            }
          }
        },
        error: (error) => {
          this.showError(`Hiba történt a módosítások mentése során: ${error.message || error}`);
        }
      });
    }

    if (hasContactChanges) {
      this.processNewContacts(element, result, onElementReload);
      if (!hasValidStatusUpdates && !hasNotes && !hasEditedPhones && !hasEditedEmails) {
        this.showSuccess('Módosítások sikeresen mentve');
      }
    }
  }

  private hasContactChanges(result: InputDialogResult): boolean {
    return !!((result.newOfficials?.length ?? 0) > 0 || result.editedOfficials ||
              result.editedPhones || result.editedEmails ||
              (result.newPhones?.length ?? 0) > 0 || (result.newEmails?.length ?? 0) > 0);
  }

  private prepareValidStatusUpdates(validStatusUpdates: any): any[] {
    if (!validStatusUpdates) return [];
    return Object.entries(validStatusUpdates).map(([contactValue, updateData]: [string, any]) => ({
      contact_value: contactValue,
      valid_status: updateData.validStatus,
      contact_type: updateData.contactType,
      is_new_contact: updateData.isNewContact || false
    }));
  }

  private processCallQualityMetrics(result: InputDialogResult): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;
    if (!result.targetPersonReached && !result.listenedFully) return;

    const weekInfo = this.callCenterStatsService.getCurrentWeekInfo();
    const dayIndex = this.callCenterStatsService.getCurrentDayIndex();

    this.callCenterStatsService.getUserWeekStats(currentUser.id, weekInfo.weekNumber, weekInfo.year).subscribe({
      next: (stats: any) => {
        const updateRequest: any = {
          user_id: currentUser.id,
          week_number: weekInfo.weekNumber,
          year: weekInfo.year,
          day_index: dayIndex,
          skip_log: true  // Adatbázisom oldal automatikus frissítése – nem kell log bejegyzés
        };
        if (result.targetPersonReached) {
          updateRequest.target_person_reached = (stats.target_person_reached?.[dayIndex] || 0) + 1;
        }
        if (result.listenedFully) {
          updateRequest.listened_completely = (stats.listened_completely?.[dayIndex] || 0) + 1;
        }
        this.callCenterStatsService.updateActualValue(updateRequest).subscribe();
      }
    });
  }

  private createReminderForCallback(element: CallCenterDatabaseTableData, result: InputDialogResult): void {
    const companyName = element.companyName || 'Ismeretlen cég';
    this.reminderService.createReminder({
      company_id: element.id,
      reminder_time: result.reminderTime!,
      title: `VH: ${companyName}`
    }).subscribe({
      next: () => {
        this.showSuccess('Emlékeztető beállítva');
        this.notificationPollingService.refreshNow();
      },
      error: (error) => this.showError(`Emlékeztető létrehozása sikertelen: ${error.message || error}`)
    });
  }

  processNewContacts(
    element: CallCenterDatabaseTableData,
    result: InputDialogResult,
    onElementReload?: (companyId: number) => void
  ): void {
    let contactsAdded = 0;
    let totalContactsToAdd = 0;

    if (result.newOfficials?.length) totalContactsToAdd += result.newOfficials.length;
    if (result.editedOfficials) totalContactsToAdd += Object.keys(result.editedOfficials).length;
    if (result.editedPhones) totalContactsToAdd += Object.keys(result.editedPhones).length;
    if (result.editedEmails) totalContactsToAdd += Object.keys(result.editedEmails).length;
    if (result.newPhones?.length) totalContactsToAdd += result.newPhones.length;
    if (result.newEmails?.length) totalContactsToAdd += result.newEmails.length;

    if (totalContactsToAdd === 0) return;

    const checkAndReload = () => {
      contactsAdded++;
      if (contactsAdded === totalContactsToAdd && onElementReload) {
        onElementReload(element.id);
      }
    };

    if (result.editedOfficials) {
      Object.keys(result.editedOfficials).forEach((oldName: string) => {
        const newName = result.editedOfficials![oldName];
        this.apiService.updateCompanyOfficialName(element.id, oldName, newName).subscribe({
          next: (response) => {
            if (response.success) this.showSuccess(`Tisztségviselő "${oldName}" átnevezve "${newName}"-re`);
            checkAndReload();
          },
          error: () => { checkAndReload(); }
        });
      });
    }

    if (result.newOfficials?.length) {
      result.newOfficials.forEach((officialName: string) => {
        this.apiService.addCompanyOfficial(element.id, officialName).subscribe({
          next: (response) => {
            if (response.success) this.showSuccess(`Tisztségviselő "${officialName}" sikeresen hozzáadva`);
            checkAndReload();
          },
          error: () => { checkAndReload(); }
        });
      });
    }

    if (result.editedPhones) {
      Object.keys(result.editedPhones).forEach((oldValue: string) => {
        const newValue = result.editedPhones![oldValue];
        this.apiService.updateCompanyContactValue(element.id, oldValue, newValue, 'phone').subscribe({
          next: (response) => {
            if (response.success) this.showSuccess(`Telefonszám "${oldValue}" módosítva "${newValue}"-re`);
            checkAndReload();
          },
          error: () => { checkAndReload(); }
        });
      });
    }

    if (result.editedEmails) {
      Object.keys(result.editedEmails).forEach((oldValue: string) => {
        const newValue = result.editedEmails![oldValue];
        this.apiService.updateCompanyContactValue(element.id, oldValue, newValue, 'email').subscribe({
          next: (response) => {
            if (response.success) this.showSuccess(`Email "${oldValue}" módosítva "${newValue}"-re`);
            checkAndReload();
          },
          error: () => { checkAndReload(); }
        });
      });
    }

    if (result.newPhones?.length) {
      result.newPhones.forEach((phoneNumber: string) => {
        this.apiService.addCompanyPhone(element.id, phoneNumber).subscribe({
          next: (response) => {
            if (response.success) this.showSuccess(`Telefonszám "${phoneNumber}" sikeresen hozzáadva`);
            checkAndReload();
          },
          error: () => { checkAndReload(); }
        });
      });
    }

    if (result.newEmails?.length) {
      this.apiService.addMultipleEmails(element.id, result.newEmails).subscribe({
        next: (responses) => {
          const successCount = responses.filter((r: any) => r.success).length;
          if (successCount > 0) this.showSuccess(`${successCount} email sikeresen hozzáadva`);
          checkAndReload();
        },
        error: () => { checkAndReload(); }
      });
    }
  }
}